import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import Markdown from '@/components/markdown/Markdown';

interface VoiceQuoteLookup {
  conversation_id: string;
  message_id?: string;
}

function TextWithClickableQuotes({ text, voiceQuotes }: { text: string; voiceQuotes?: VoiceQuoteLookup[] }) {
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  const messageIdMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!voiceQuotes) return map;
    for (const vq of voiceQuotes) {
      if (vq.conversation_id && vq.message_id && !map.has(vq.conversation_id)) {
        map.set(vq.conversation_id, vq.message_id);
      }
    }
    return map;
  }, [voiceQuotes]);

  const hasQuotes = /<quote>/.test(text);

  // Extract citation data from the content inside a <quote> tag.
  // Handles multiple AI output formats:
  //   {conversationId: abc, respondent_index: 3}   — braces + key
  //   conversationId: abc, respondent_index: 3      — no braces, with key
  //   abc, respondent_index: 3                      — no braces, no key
  //   "Dancing brings people together"              — actual quote text (not a citation)
  const parseCitation = (raw: string): { conversationId: string; respondentIndex: number } | null => {
    const stripped = raw.replace(/^\s*\{?\s*|\s*\}?\s*$/g, '');

    // Must have respondent_index to be a citation
    const indexMatch = stripped.match(/respondent_index:\s*(\d+)/);
    if (!indexMatch) return null;

    // Extract conversationId — either as key:value or as leading 24-char hex
    const keyMatch = stripped.match(/conversationId:\s*([a-f0-9]{24})/i);
    const bareMatch = stripped.match(/^([a-f0-9]{24})/);
    const conversationId = keyMatch?.[1] || bareMatch?.[1];
    if (!conversationId) return null;

    return { conversationId, respondentIndex: parseInt(indexMatch[1], 10) };
  };

  const parseText = (input: string) => {
    // Match <quote>...</quote> with any content (braces optional)
    const regex = /<quote>\s*([\s\S]*?)\s*<\/quote>/g;
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(input)) !== null) {
      const citation = parseCitation(match[1]);

      if (!citation) {
        // Not a citation tag (e.g., actual quote text) — skip the tag entirely
        lastIndex = regex.lastIndex;
        continue;
      }

      const before = input.slice(lastIndex, match.index);
      if (before) {
        result.push(
          <Markdown key={uuidv4()} isParagraphSpanEl={hasQuotes}>
            {before}
          </Markdown>,
        );
      }

      result.push(
        <button
          key={uuidv4()}
          type="button"
          onClick={() => {
            const messageId = messageIdMap.get(citation.conversationId);
            openTranscriptPanel(citation.conversationId, messageId);
          }}
          className="inline-flex items-center justify-center min-w-5 h-5 px-1 mx-0.5 font-semibold text-[11px] text-white rounded-full bg-emerald-500 opacity-80 hover:opacity-100 transition-opacity align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-1"
        >
          {citation.respondentIndex}
        </button>,
      );

      lastIndex = regex.lastIndex;
    }

    const after = input.slice(lastIndex);
    if (after)
      result.push(
        <Markdown key={uuidv4()} isParagraphSpanEl={hasQuotes}>
          {after}
        </Markdown>,
      );

    return result;
  };

  // When quotes are present, render inline so badges flow with text
  // When no quotes, render as block for proper paragraph spacing
  if (hasQuotes) {
    return <span className="leading-relaxed">{parseText(text)}</span>;
  }

  return parseText(text);
}

export default TextWithClickableQuotes;
